import { useState } from "react";
import {
  FileText,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { StatusBadge } from "./StatusBadge";

export interface Evidence {
  id: string;
  title: string;
  source: string;
  type: "document" | "screenshot" | "log" | "api" | "config";
  status: "pass" | "fail" | "pending";
  collectedAt: string;
  controlId?: string;
  description?: string;
  url?: string;
}

const typeLabels: Record<Evidence["type"], string> = {
  document: "Document",
  screenshot: "Screenshot",
  log: "Log",
  api: "API Response",
  config: "Config",
};

const typeIcons: Record<Evidence["type"], React.ElementType> = {
  document: FileText,
  screenshot: FileText,
  log: FileText,
  api: ExternalLink,
  config: FileText,
};

interface EvidenceViewerProps {
  evidence: Evidence[];
  compact?: boolean;
}

export function EvidenceViewer({
  evidence,
  compact = false,
}: EvidenceViewerProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const passCount = evidence.filter((e) => e.status === "pass").length;
  const failCount = evidence.filter((e) => e.status === "fail").length;

  return (
    <div className="space-y-2">
      {!compact && (
        <div className="flex items-center gap-4 text-sm d mb-3">
          <span className="flex items-center gap-1.5">
            <CheckCircle2 className="size-4 text-emerald-500" />
            {passCount} passing
          </span>
          <span className="flex items-center gap-1.5">
            <XCircle className="size-4 text-red-500" />
            {failCount} failing
          </span>
          <span className="flex items-center gap-1.5">
            <Clock className="size-4 text-amber-500" />
            {evidence.filter((e) => e.status === "pending").length} pending
          </span>
        </div>
      )}

      {evidence.map((ev) => {
        const Icon = typeIcons[ev.type];
        const isExpanded = expanded.has(ev.id);
        return (
          <div
            key={ev.id}
            className="rounded-lg border border-border overflow-hidden"
          >
            <div
              className="flex items-center gap-3 p-3 cursor-pointer hover:bg-accent/50 transition-colors"
              onClick={() => toggle(ev.id)}
            >
              <Icon className="size-4 d flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium truncate">
                    {ev.title}
                  </span>
                  {ev.controlId && (
                    <span className="text-xs font-mono d">{ev.controlId?.toUpperCase()}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs d">{ev.source}</span>
                  <span className="text-xs d">·</span>
                  <span className="text-xs d">{typeLabels[ev.type]}</span>
                  <span className="text-xs d">·</span>
                  <span className="text-xs d">{ev.collectedAt}</span>
                </div>
              </div>
              <StatusBadge status={ev.status} />
              {isExpanded ? (
                <ChevronUp className="size-4 d flex-shrink-0" />
              ) : (
                <ChevronDown className="size-4 d flex-shrink-0" />
              )}
            </div>
            {isExpanded && ev.description && (
              <div className="px-3 pb-3 border-t border-border bg-muted/20">
                <p className="text-sm d mt-2">{ev.description}</p>
                {ev.url && (
                  <a
                    href={ev.url}
                    className="inline-flex items-center gap-1 text-xs text-foreground underline mt-2"
                  >
                    View source <ExternalLink className="size-3" />
                  </a>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
